import React, { useEffect, useState, useCallback, useMemo } from 'react';
import './MinisiteShared.css';

const LETTERS = [...'אבגדהוזחטיכלמנסעפצקרשתםףץןך'];
const COLS = 9;

function buildCells() {
  const cells = LETTERS.map((ch) => ({ type: 'char', ch }));
  cells.push({ type: 'space' }, { type: 'del' }, { type: 'done' });
  return cells;
}

const MAX_NAME = 24;

/**
 * @param {object} props
 * @param {string} props.value
 * @param {(s: string) => void} props.onChange
 * @param {() => void} props.onDone
 * @param {() => void} [props.onCancel]
 */
function RemoteNameKeyboard({ value, onChange, onDone, onCancel }) {
  const cells = useMemo(buildCells, []);
  const [idx, setIdx] = useState(0);

  const rows = useMemo(() => {
    const r = [];
    for (let i = 0; i < cells.length; i += COLS) {
      r.push(cells.slice(i, i + COLS));
    }
    return r;
  }, [cells]);

  const posFromIndex = useCallback(
    (linear) => {
      const row = Math.floor(linear / COLS);
      const col = linear % COLS;
      return { row, col, linear };
    },
    []
  );

  const indexFromRC = useCallback((row, col) => row * COLS + col, []);

  const move = useCallback(
    (dr, dc) => {
      setIdx((prev) => {
        const { row, col } = posFromIndex(prev);
        let nr = row + dr;
        let nc = col + dc;
        const maxRow = rows.length - 1;
        if (nr < 0) nr = maxRow;
        if (nr > maxRow) nr = 0;
        if (dr !== 0) {
          nc = Math.min(col, rows[nr].length - 1);
        } else {
          const width = rows[nr].length;
          if (nc < 0) nc = width - 1;
          if (nc >= width) nc = 0;
        }
        if (!rows[nr][nc]) {
          return prev;
        }
        return indexFromRC(nr, nc);
      });
    },
    [posFromIndex, rows, indexFromRC]
  );

  const activate = useCallback(() => {
    const cell = cells[idx];
    if (!cell) return;
    if (cell.type === 'char') {
      if (value.length < MAX_NAME) onChange(value + cell.ch);
      return;
    }
    if (cell.type === 'space') {
      if (value.length < MAX_NAME) onChange(`${value} `);
      return;
    }
    if (cell.type === 'del') {
      onChange(value.slice(0, -1));
      return;
    }
    if (cell.type === 'done' && value.trim()) {
      onDone();
    }
  }, [cells, idx, value, onChange, onDone]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') {
        move(0, -1);
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowLeft') {
        move(0, 1);
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowDown') {
        move(1, 0);
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowUp') {
        move(-1, 0);
        e.preventDefault();
        return;
      }
      if (e.key === 'Enter' || e.keyCode === 13) {
        activate();
        e.preventDefault();
        return;
      }
      if (e.key === 'Backspace') {
        if (value.length) {
          onChange(value.slice(0, -1));
          e.preventDefault();
          return;
        }
        if (onCancel) {
          onCancel();
          e.preventDefault();
        }
        return;
      }
      if (e.key === 'Escape' && onCancel) {
        onCancel();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [move, activate, value, onCancel, onChange]);

  return (
    <div className="minisite-name-kb" dir="rtl">
      <p className="minisite-name-preview">{value || '—'}</p>
      <div className="minisite-name-grid">
        {rows.map((row, ri) => (
          <div key={ri} className="minisite-name-row">
            {row.map((cell, ci) => {
              const linear = indexFromRC(ri, ci);
              const sel = linear === idx;
              if (!cell) return <span key={ci} className="minisite-name-cell minisite-name-cell--empty" />;
              let label = cell.ch;
              if (cell.type === 'space') label = 'רווח';
              if (cell.type === 'del') label = 'מחק';
              if (cell.type === 'done') label = 'סיום';
              return (
                <button
                  type="button"
                  key={`${ri}-${ci}`}
                  className={`minisite-name-cell ${sel ? 'minisite-name-cell--focus' : ''}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <p className="minisite-name-hint">חצים לבחירה · Enter ללחיצה · Esc לביטול</p>
    </div>
  );
}

export default RemoteNameKeyboard;
